import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AlertTriangle,
  ArrowDownUp,
  ArrowLeftRight,
  Banknote,
  BarChart,
  BarChart3,
  BookOpen,
  Brain,
  Bug,
  Building2,
  CalendarClock,
  CalendarDays,
  Calculator,
  Camera,
  Car,
  CheckSquare,
  ClipboardList,
  CloudUpload,
  CreditCard,
  DollarSign,
  FileCheck,
  FileSignature,
  FileSpreadsheet,
  FileStack,
  FileText,
  FolderKanban,
  FolderOpen,
  Fuel,
  Gavel,
  Globe,
  HardDrive,
  History,
  Inbox,
  Landmark,
  LayoutDashboard,
  LayoutTemplate,
  Library,
  Lightbulb,
  Lock,
  Mail,
  Megaphone,
  MessageSquare,
  Package,
  Percent,
  Receipt,
  Repeat,
  Scale,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Store,
  Target,
  Timer,
  Trash2,
  TrendingUp,
  Truck,
  UserCheck,
  Users,
  UsersRound,
  Wallet,
  Wrench,
  Zap,
} from "lucide-react";

export type DashboardRole = "user" | "admin" | "manager" | "crew" | "subcontractor" | "sales" | string;

export type MenuItem = {
  icon: LucideIcon;
  label: string;
  path: string;
};

export type MenuGroup = {
  label: string;
  key: string;
  items: MenuItem[];
};

export const ORIGINAL_NAVIGATION_KEY = "darkhorse_show_original_navigation";

/**
 * This is the pre-cleanup navigation inventory. The visibility helpers below
 * intentionally retain its role and feature-toggle behavior so the simplified
 * view is presentation-only, not a permission system.
 */
export const legacyMainItems: MenuItem[] = [
  { icon: Target, label: "Command Center", path: "/command-center" },
  { icon: LayoutDashboard, label: "Dashboard", path: "/" },
  // OWNER_GROWTH_EXPORT_BEGIN
  { icon: Megaphone, label: "Leads & Outreach", path: "/leads-outreach" },
  // OWNER_GROWTH_EXPORT_END
  { icon: MessageSquare, label: "Client Inbox", path: "/client-inbox" },
  { icon: BarChart3, label: "Company Metrics", path: "/company-dashboard" },
  { icon: FolderKanban, label: "Projects", path: "/projects" },
  { icon: Landmark, label: "Banking", path: "/banking" },
];

export const legacyMenuGroups: MenuGroup[] = [
  {
    label: "Pipeline",
    key: "pipeline",
    items: [
      { icon: Users, label: "Leads", path: "/leads" },
      { icon: Inbox, label: "Bid Tracker", path: "/bid-tracker" },
      { icon: Gavel, label: "Bid Management", path: "/bid-management" },
      { icon: Globe, label: "Bid Crawl Sites", path: "/lead-sources" },
      { icon: Megaphone, label: "Realtor Outreach", path: "/realtor-outreach" },
      { icon: Library, label: "Templates", path: "/templates" },
    ],
  },
  {
    label: "Money In",
    key: "money-in",
    items: [
      { icon: CreditCard, label: "Invoices", path: "/invoices" },
      { icon: FileCheck, label: "Change Orders", path: "/change-orders" },
      { icon: Percent, label: "Commissions", path: "/commissions" },
    ],
  },
  {
    label: "Expenses",
    key: "expenses",
    items: [
      { icon: Receipt, label: "Receipts", path: "/receipts" },
      { icon: DollarSign, label: "Bills", path: "/bills" },
      { icon: ShoppingCart, label: "Purchase Orders", path: "/purchase-orders" },
      { icon: Repeat, label: "Recurring Expenses", path: "/recurring-expenses" },
      { icon: History, label: "Payment History (outbound)", path: "/payment-history" },
      { icon: Wallet, label: "Stripe Dashboard", path: "/stripe-dashboard" },
    ],
  },
  {
    label: "Financial Reports",
    key: "financial-reports",
    items: [
      { icon: TrendingUp, label: "Profit & Loss", path: "/profit-loss" },
      { icon: Scale, label: "Balance Sheet", path: "/balance-sheet" },
      { icon: ArrowLeftRight, label: "Reconciliation", path: "/reconciliation" },
      { icon: BookOpen, label: "Account Register", path: "/account-register" },
      { icon: CalendarClock, label: "AR/AP Aging", path: "/aging-report" },
      { icon: FileText, label: "Journal Entries", path: "/journal-entries" },
      { icon: BarChart3, label: "Job Costing", path: "/job-costing" },
      { icon: FileSpreadsheet, label: "Financial Summary", path: "/financial-summary" },
      { icon: FileText, label: "1099", path: "/1099-report" },
      { icon: History, label: "Historical P&L (QB)", path: "/historical-pl" },
    ],
  },
  {
    label: "Operations",
    key: "operations",
    items: [
      { icon: ClipboardList, label: "Daily Reports", path: "/daily-reports" },
      { icon: UsersRound, label: "Crew Reports", path: "/crew-reports" },
      { icon: FileSignature, label: "Submittals", path: "/submittals" },
      { icon: FileSignature, label: "Lien Waivers", path: "/lien-waivers" },
      { icon: Shield, label: "Warranties", path: "/warranties" },
      { icon: ShieldAlert, label: "WC Report", path: "/wc-report" },
      { icon: ShieldCheck, label: "Compliance", path: "/compliance" },
      { icon: CalendarDays, label: "Schedule", path: "/schedule" },
      { icon: Timer, label: "Time Tracking", path: "/time-tracking" },
      { icon: FileText, label: "Daily Cost Log", path: "/daily-cost-log" },
      { icon: Receipt, label: "Cost Plus Invoice", path: "/cost-plus-invoice" },
      { icon: FolderOpen, label: "Master Lists", path: "/master-lists" },
      { icon: FileSignature, label: "Form Templates", path: "/form-templates" },
      { icon: Camera, label: "Unassigned Photos", path: "/unassigned-photos" },
    ],
  },
  {
    label: "Financial Setup",
    key: "financial-setup",
    items: [
      { icon: Landmark, label: "Chart of Accounts", path: "/chart-of-accounts" },
      { icon: ArrowDownUp, label: "Opening Balances", path: "/opening-balances" },
      { icon: AlertTriangle, label: "Budget Alerts", path: "/budget-alerts" },
      { icon: Truck, label: "Fixed Assets", path: "/fixed-assets" },
    ],
  },
  {
    label: "Data & Setup",
    key: "data",
    items: [
      { icon: Package, label: "Materials Catalog", path: "/materials" },
      { icon: Wrench, label: "Labor Rates", path: "/labor-rates" },
      { icon: Store, label: "Vendors", path: "/vendors" },
      { icon: HardDrive, label: "Subcontractors", path: "/subcontractors" },
      { icon: UsersRound, label: "Customers", path: "/customers" },
      { icon: Building2, label: "Brands", path: "/brands" },
      { icon: Brain, label: "AI Learning", path: "/ai-learning" },
    ],
  },
  {
    label: "Social Media",
    key: "social",
    items: [
      { icon: Sparkles, label: "Content Hub", path: "/social-media" },
      { icon: Globe, label: "Social Accounts", path: "/social-accounts" },
    ],
  },
  {
    label: "Materials & Orders",
    key: "materials-orders",
    items: [
      { icon: ClipboardList, label: "Order Lists", path: "/order-lists" },
      { icon: CreditCard, label: "Vendor Credits", path: "/vendor-credits" },
      { icon: BarChart, label: "Material Tracking", path: "/material-tracking" },
      { icon: Car, label: "Mileage Tracker", path: "/mileage" },
    ],
  },
  {
    label: "Financial Tools",
    key: "financial-tools",
    items: [
      { icon: Banknote, label: "Loans & Debt", path: "/loans" },
      { icon: Percent, label: "Sales Tax", path: "/sales-tax" },
      { icon: Fuel, label: "Cost Trends", path: "/cost-trends" },
      { icon: UserCheck, label: "CPA Access", path: "/accountant-access" },
      { icon: Bug, label: "Issues & Feedback", path: "/issues" },
      { icon: Shield, label: "Audit Trail", path: "/audit-history" },
    ],
  },
  {
    label: "AI Tools",
    key: "ai-tools",
    items: [
      { icon: Brain, label: "Intelligence Hub", path: "/intelligence" },
      { icon: Fuel, label: "Cost Trend Analyzer", path: "/cost-trends" },
      { icon: Lightbulb, label: "AI Insights", path: "/ai-insights" },
      { icon: Zap, label: "Action Queue", path: "/action-queue" },
      { icon: MessageSquare, label: "AI Assistant", path: "/ai-chat" },
      { icon: BarChart3, label: "Summary Reports", path: "/summary-reports" },
    ],
  },
  {
    label: "Tools",
    key: "tools",
    items: [
      { icon: BookOpen, label: "Employee Handbook", path: "/handbook" },
      { icon: LayoutTemplate, label: "Project Templates", path: "/project-templates" },
      { icon: FileSignature, label: "Client Approvals", path: "/client-approvals" },
      { icon: Mail, label: "Email Templates", path: "/email-templates" },
    ],
  },
];

export const adminGroup: MenuGroup = {
  label: "Admin",
  key: "admin",
  items: [
    { icon: Settings, label: "User Management", path: "/user-management" },
    { icon: Building2, label: "Company Profile", path: "/company-settings" },
    { icon: ShieldCheck, label: "Deletion Requests", path: "/deletion-requests" },
    { icon: Activity, label: "Activity Log", path: "/activity-log" },
    { icon: CloudUpload, label: "Backup & Restore", path: "/backup" },
    { icon: Shield, label: "Security Center", path: "/security-center" },
    { icon: CheckSquare, label: "Data Integrity", path: "/data-integrity" },
    { icon: Lock, label: "Field Permissions", path: "/field-permissions" },
    { icon: Mail, label: "Communications", path: "/communications" },
    { icon: Trash2, label: "Trash", path: "/trash" },
    { icon: FileSpreadsheet, label: "QuickBooks Migration", path: "/quickbooks-migration" },
  ],
};

export const subPortalItems: MenuItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/" },
  { icon: DollarSign, label: "My Bills", path: "/sub-portal/bills" },
  { icon: CreditCard, label: "My Payments", path: "/sub-portal/payments" },
  { icon: FolderKanban, label: "My Projects", path: "/sub-portal/projects" },
];

export const crewMainItems: MenuItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/" },
  { icon: FolderKanban, label: "Projects", path: "/projects" },
  { icon: ClipboardList, label: "Daily Reports", path: "/daily-reports" },
  { icon: Receipt, label: "Receipts", path: "/receipts" },
];

const conciseMainItems: MenuItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/" },
  { icon: MessageSquare, label: "Client Inbox", path: "/client-inbox" },
  // OWNER_GROWTH_EXPORT_BEGIN
  { icon: Megaphone, label: "Leads & Outreach", path: "/leads-outreach" },
  // OWNER_GROWTH_EXPORT_END
  { icon: Users, label: "Leads", path: "/leads" },
  { icon: FolderKanban, label: "Projects", path: "/projects" },
  { icon: UsersRound, label: "Customers", path: "/customers" },
  { icon: Calculator, label: "Estimates", path: "/estimator" },
  { icon: FileText, label: "Scope of Work", path: "/scope-of-work" },
  { icon: CreditCard, label: "Invoices", path: "/invoices" },
  { icon: Landmark, label: "Banking", path: "/banking" },
  { icon: Receipt, label: "Receipts", path: "/receipts" },
  { icon: DollarSign, label: "Bills", path: "/bills" },
  { icon: FileStack, label: "Subcontractor Bills", path: "/sub-bills" },
  { icon: ShieldCheck, label: "Compliance", path: "/compliance" },
  { icon: ShieldAlert, label: "WC Report", path: "/wc-report" },
  { icon: FolderOpen, label: "Media Library", path: "/media-library" },
  { icon: Package, label: "Materials Catalog", path: "/materials" },
];

const salesConcisePaths = new Set([
  "/",
  "/client-inbox",
  "/leads",
  // OWNER_GROWTH_EXPORT_BEGIN
  "/leads-outreach",
  // OWNER_GROWTH_EXPORT_END
  "/projects",
  "/estimator",
  "/scope-of-work",
]);

function withoutHidden(items: MenuItem[], hiddenPaths: ReadonlySet<string>): MenuItem[] {
  return items.filter((item) => !hiddenPaths.has(item.path));
}

/** Matches the legacy sidebar's direct-item visibility exactly. */
export function getVisibleLegacyMainItems(role: DashboardRole, hiddenPaths: ReadonlySet<string>): MenuItem[] {
  let items: MenuItem[];
  if (role === "subcontractor") items = subPortalItems;
  else if (role === "crew") items = crewMainItems;
  else if (role !== "admin") items = legacyMainItems.filter((item) => item.path !== "/banking");
  else items = legacyMainItems;
  return withoutHidden(items, hiddenPaths);
}

/** Matches the legacy sidebar's grouped-item visibility exactly. */
export function getVisibleLegacyGroups(role: DashboardRole, hiddenPaths: ReadonlySet<string>): MenuGroup[] {
  if (role === "crew" || role === "subcontractor") return [];

  let groups: MenuGroup[];
  if (role === "sales") {
    groups = legacyMenuGroups.filter((group) => group.key === "pipeline" || group.key === "reports");
  } else if (role === "admin" || role === "user") {
    groups = [...legacyMenuGroups, adminGroup];
  } else {
    groups = legacyMenuGroups;
  }

  return groups
    .map((group) => ({
      ...group,
      items: withoutHidden(group.items, hiddenPaths).filter(
        (item) => item.path !== "/realtor-outreach" || role === "admin" || role === "user",
      ),
    }))
    .filter((group) => group.items.length > 0);
}

/** The concise sidebar keeps crew and subcontractor navigation unchanged. */
export function getVisibleConciseMainItems(role: DashboardRole, hiddenPaths: ReadonlySet<string>): MenuItem[] {
  if (role === "crew" || role === "subcontractor") {
    return getVisibleLegacyMainItems(role, hiddenPaths);
  }

  const roleItems = role === "sales"
    ? conciseMainItems.filter((item) => salesConcisePaths.has(item.path))
    : conciseMainItems.filter((item) => item.path !== "/banking" || role === "admin");

  return withoutHidden(roleItems, hiddenPaths);
}

/** Realtor Outreach is deliberately surfaced only as a personal owner/admin shortcut. */
export function getPersonalToolItems(role: DashboardRole, hiddenPaths: ReadonlySet<string>): MenuItem[] {
  if (role !== "admin" && role !== "user") return [];
  return withoutHidden(
    [{ icon: Megaphone, label: "Realtor Outreach", path: "/realtor-outreach" }],
    hiddenPaths,
  );
}

/**
 * The searchable overflow contains every visible legacy item that is not
 * already in concise navigation or Personal Tools. It changes discoverability,
 * not route registration or server authorization.
 */
export function getAdditionalToolGroups(role: DashboardRole, hiddenPaths: ReadonlySet<string>): MenuGroup[] {
  if (role === "crew" || role === "subcontractor") return [];

  const concisePaths = new Set(getVisibleConciseMainItems(role, hiddenPaths).map((item) => item.path));
  const personalPaths = new Set(getPersonalToolItems(role, hiddenPaths).map((item) => item.path));
  const excludePaths = new Set<string>(Array.from(concisePaths).concat(Array.from(personalPaths)));
  const additionalMainItems = getVisibleLegacyMainItems(role, hiddenPaths)
    .filter((item) => !excludePaths.has(item.path));
  const additionalGroups = getVisibleLegacyGroups(role, hiddenPaths)
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !excludePaths.has(item.path)),
    }))
    .filter((group) => group.items.length > 0);

  return additionalMainItems.length > 0
    ? [{ label: "Workspace", key: "workspace", items: additionalMainItems }, ...additionalGroups]
    : additionalGroups;
}

export function filterToolGroups(groups: MenuGroup[], query: string): MenuGroup[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return groups;

  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        `${group.label} ${item.label} ${item.path}`.toLocaleLowerCase().includes(normalizedQuery),
      ),
    }))
    .filter((group) => group.items.length > 0);
}

export function isNavigationItemActive(path: string, location: string): boolean {
  return location === path || (path !== "/" && location.startsWith(path));
}

export function getAllNavigationItems(): MenuItem[] {
  return [
    ...conciseMainItems,
    ...legacyMainItems,
    ...legacyMenuGroups.flatMap((group) => group.items),
    ...adminGroup.items,
    ...subPortalItems,
    ...crewMainItems,
  ];
}
